import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Notification } from '../models/Notification.model'
import NotificationService from '../services/NotificationService/NotificationService'
import { useWebSocket } from '../hooks/useWebSocket'

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  markAsRead: (notificationId: string | number) => Promise<void>
  markAllAsRead: () => Promise<void>
  refreshNotifications: () => Promise<void>
}

export const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  markAsRead: async () => {
    throw new Error('NotificationContext must be used within NotificationProvider')
  },
  markAllAsRead: async () => {
    throw new Error('NotificationContext must be used within NotificationProvider')
  },
  refreshNotifications: async () => {
    throw new Error('NotificationContext must be used within NotificationProvider')
  },
})

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider = ({ children }: NotificationProviderProps): JSX.Element => {
  const { isAuthenticated } = useAuth0()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const hasFetchedRef = useRef(false)
  // Memoize the service instance to prevent recreating it on every render
  const notificationService = React.useMemo(() => new NotificationService(), [])

  // Calculate unreadCount from notifications array to ensure accuracy
  const unreadCount = React.useMemo(() => {
    return notifications.filter((n) => !n.read).length
  }, [notifications])

  // Determine WebSocket URL based on environment - memoize to prevent re-renders
  const webSocketUrl = React.useMemo(() => {
    // Allow explicit WebSocket URL override via environment variable
    if (process.env.REACT_APP_WEBSOCKET_URL) {
      return process.env.REACT_APP_WEBSOCKET_URL
    }

    const apiUrl = process.env.REACT_APP_API_URL || ''
    if (apiUrl.startsWith('https://')) {
      // Production: use wss:// (replace https:// with wss:// and remove /api if present)
      const baseUrl = apiUrl.replace('https://', '').replace('/api', '')
      return `wss://${baseUrl}`
    } else {
      // Local development: use ws://localhost:3001 as specified in requirements
      return 'ws://localhost:3001'
    }
  }, [])

  // Check if notifications feature is enabled (optional env var to disable)
  const notificationsEnabled = process.env.REACT_APP_NOTIFICATIONS_ENABLED !== 'false'

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !notificationsEnabled) {
      return
    }

    try {
      setLoading(true)
      const response = await notificationService.getNotifications()
      console.log('Fetched notifications:', response)
      setNotifications(response.notifications || [])
      // unreadCount will be automatically calculated from notifications array
    } catch (error: any) {
      console.error('Error fetching notifications:', error)
      // Handle 404 gracefully (endpoint might not be implemented yet)
      if (error?.status === 404) {
        // Silently handle 404 - backend endpoint not available yet
        setNotifications([])
      }
      // Silently handle all other errors (backend might not be ready)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, notificationService, notificationsEnabled])

  const handleWebSocketMessage = useCallback(
    (notification: Notification) => {
      // Add new notification to the beginning of the list
      // unreadCount will be automatically recalculated from the updated array
      setNotifications((prev) => [notification, ...prev])
    },
    [],
  )

  // Set up WebSocket connection (only if notifications are enabled)
  useWebSocket({
    url: webSocketUrl,
    onMessage: handleWebSocketMessage,
    onError: () => {
      // Silently handle WebSocket errors (server might not be running)
      // Errors are already logged in useWebSocket hook
    },
    enabled: isAuthenticated && notificationsEnabled,
  })

  // Fetch notifications on mount and when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      fetchNotifications()
    }
    // Reset fetch flag when authentication changes
    if (!isAuthenticated) {
      hasFetchedRef.current = false
      setNotifications([])
      // unreadCount will be automatically recalculated (will be 0 when array is empty)
    }
  }, [isAuthenticated, fetchNotifications])

  // Poll for new notifications every 30 seconds so real-time events (e.g. new
  // hospital/agency requests) are picked up even when the WebSocket is silent
  useEffect(() => {
    if (!isAuthenticated || !notificationsEnabled) return

    const pollInterval = setInterval(() => {
      fetchNotifications()
    }, 30000)

    return () => clearInterval(pollInterval)
  }, [isAuthenticated, notificationsEnabled, fetchNotifications])

  const markAsRead = useCallback(
    async (notificationId: string | number) => {
      try {
        await notificationService.markAsRead(notificationId)
        // Update local state - unreadCount will be automatically recalculated
        setNotifications((prev) =>
          prev.map((notif) => (notif.id === notificationId ? { ...notif, read: true } : notif)),
        )
      } catch (error) {
        // Silently handle errors (backend might not be ready)
      }
    },
    [notificationService],
  )

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead()
      // Update local state - unreadCount will be automatically recalculated (will be 0)
      setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })))
    } catch (error) {
      // Silently handle errors (backend might not be ready)
    }
  }, [notificationService])

  const refreshNotifications = useCallback(async () => {
    hasFetchedRef.current = false // Allow refresh to fetch again
    await fetchNotifications()
  }, [fetchNotifications])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}
